/**
 * The packing-stations assignment list (GET /warehouses/:id/shipnodes) holds paired stations from the ShipNode
 * backend (`legacy: false`): `name`, `online`, `printers[]` with `displayName`, `online`, `enabled`. Anything else
 * (the 2023 apps' Firestore computers, retired in EN-3208) is dropped. Stations are chosen by id: the machine id,
 * which is what the warehouse saves as `computerId`.
 */

export type PickerPrinter = { id: string; name: string; online: boolean };

export type PickerStation = {
  id: string;
  name: string;
  online: boolean;
  printers: PickerPrinter[];
};

type Entry = {
  id?: unknown;
  name?: string | null;
  displayName?: string | null;
  online?: unknown;
  enabled?: unknown;
  legacy?: unknown;
  printers?: unknown;
};

const asList = (printers: unknown): Entry[] => {
  if (Array.isArray(printers)) return printers.filter(Boolean);
  if (printers && typeof printers === 'object') {
    return Object.values(printers as Record<string, Entry>).filter(Boolean);
  }
  return [];
};

const toPaired = (entry: Entry): PickerStation => ({
  id: String(entry.id),
  name: entry.name || String(entry.id),
  online: entry.online === true,
  /* A printer switched off in Skupreme can't take a label, so it isn't offered. */
  printers: asList(entry.printers)
    .filter(p => p.id && p.enabled !== false)
    .map(p => ({
      id: String(p.id),
      name: p.displayName || String(p.id),
      online: p.online === true,
    })),
});

export function normalizeShipnodeEntries(entries: unknown): PickerStation[] {
  if (!Array.isArray(entries)) return [];
  return entries
    .filter((entry): entry is Entry => Boolean(entry?.id))
    .filter(entry => entry.legacy === false)
    .map(toPaired);
}
